import { DriveOdataItem, SiteDriveOdataItem, SiteOdataItem, TeamItem } from '@common/models';
import { CustomTreeElement, DriveTreeElement, SiteItemTreeElement, TeamTreeElement } from '@common/types';
import { TreeComponent, TreeElement } from 'mbs-ui-kit';
import { MonoTypeOperatorFunction, Observable, throwError } from 'rxjs';
import { switchMap, takeUntil } from 'rxjs/operators';

type TreeElementWithChildren<T> = { id: string; children?: T[] };

export function createTreeElementFromDrive(item: DriveOdataItem): DriveTreeElement {
  return {
    id: item.Id,
    label: item.Name,
    value: item,
    checked: true
  };
}

export function createTreeFromDrives(items: DriveOdataItem[]): DriveTreeElement[] {
  return items.map(createTreeElementFromDrive);
}

export function createTreeElementFromSiteItem(item: SiteDriveOdataItem): SiteItemTreeElement {
  return {
    id: item.Id,
    label: item.Name,
    value: item,
    checked: item.BackupOn
  };
}

export function createTreeFromSiteItem(items: SiteDriveOdataItem[] | SiteOdataItem[]): SiteItemTreeElement[] {
  return items.map((item) => ({
    id: item.Id,
    label: item.Name,
    value: item,
    checked: item.BackupOn
  }));
}

export function createTreeElementFromTeamItem(item: TeamItem): TeamTreeElement {
  return {
    id: item.Id,
    label: item.Name,
    value: item
  };
}

export function createTreeFromTeamItem(items: TeamItem[]): TeamTreeElement[] {
  return items.map(createTreeElementFromTeamItem);
}

export function getTreeElementById<T extends TreeElementWithChildren<T>>(tree: T[], id: string): T | undefined {
  for (const item of tree) {
    const temp = getTreeElementInChildren(item, id);

    if (temp != undefined) {
      return temp;
    }
  }

  return void 0;
}

function getTreeElementInChildren<T extends TreeElementWithChildren<T>>(tree: T, id: string): T | null {
  if (tree.id === id) {
    return tree;
  }

  if (tree.children) {
    for (const item of tree.children) {
      if (item.id === id) {
        return item;
      } else {
        const temp = getTreeElementInChildren(item, id);

        if (temp != undefined) {
          return temp;
        }
      }
    }
  }

  return null;
}

export function getArrayParentsForTreeElement<T = any>(treeElement: CustomTreeElement<T>): CustomTreeElement<T>[] {
  let temp = treeElement;
  const pathArray: CustomTreeElement<T>[] = [];

  while (temp.parent) {
    pathArray.push(temp);
    temp = temp.parent;
  }

  pathArray.push(temp);

  return pathArray;
}

export function getDriveIdOfTreeElement(treeElement: DriveTreeElement): string | null {
  let temp = treeElement;

  while (temp.parent && !temp.value?.DriveId) {
    temp = temp.parent;
  }

  return temp.value?.DriveId || null;
}

export function loadTreeElementSubtree(treeComponent: TreeComponent, newTreeElement: TreeElement): void {
  const { id } = newTreeElement;
  let treeElement = treeComponent.getItemById(id);

  if (!treeElement) {
    treeElement = getTreeElementById(treeComponent.data, id);

    if (!treeElement) {
      const [parent] = treeComponent.getSelected();

      if (!parent) return;

      treeElement = newTreeElement;
      parent.children = [...(parent.children ?? []), treeElement];
    }

    treeComponent.rebuildFlatData();
  }

  treeComponent.getSubtree(treeElement, false);
}

export function cancelTreeRequestOn<T>(cancel$: Observable<void>): MonoTypeOperatorFunction<T> {
  // mbs-tree resets the node loading state only on error: a plain completion would leave the node spinning and the tree disabled
  return takeUntil(cancel$.pipe(switchMap(() => throwError(() => new Error('Tree request cancelled')))));
}
